import MuxPlayer from '@mux/mux-player-react';
import { PREVIEW_WINDOW_SECONDS } from '@src/utility/frameExtraction';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

import type MuxPlayerElement from '@mux/mux-player';
import type { MuxPlayerElementEventMap } from '@mux/mux-player';
import type { CSSProperties } from 'react';

/** `timeupdate` lands ~4x a second, so the window end is tested with slack. */
const TIME_UPDATE_PADDING_SECONDS = 0.265;

/*
 * A speaker whose first word falls inside the last six seconds has a window
 * longer than what is left of the story, so `timeupdate` never reaches the end
 * of it and the loop above never fires — the element simply runs out. That is
 * what `onEnded` is for: without it the row would keep showing pause over a
 * stopped player, and the next play would restart the story from zero, since
 * `play()` on an ended element rewinds it.
 */

/** No chrome: this plays in a 96px tile, which has room for none of it. */
const playerStyle: CSSProperties & { [key: `--${string}`]: string } = {
  width: '100%',
  height: '100%',
  '--controls': 'none',
  '--media-object-fit': 'cover',
};

interface SpeakerMomentPreviewProps {
  /** Direct MP4 for the story's original video, when it has one. */
  mp4Url?: string;
  /** Mux id, used only when there is no MP4 (see `resolveStoryPlaybackSource`). */
  muxPlaybackId?: string;
  /** Where the speaker's turn begins, in story seconds. */
  startTime: number;
  /** Where it ends. The window never runs past it — see below. */
  endTime: number;
  /** The still already on screen, so the tile does not open on black. */
  poster?: string;
  label: string;
  /** Play, or hold where it is. */
  isPlaying: boolean;
  /** The story ran out before the window did — the row goes back to its still. */
  onEnded: () => void;
}

/**
 * The moment a speaker starts talking, looping in the mapping row.
 *
 * Deliberately **not** the Add Clips picker's `ClipRowPreviewPlayer`: that one
 * is built around a candidate's passages — an ordered list of source ranges, a
 * handover between them, and time reported back on the clip's own clock for the
 * row's transcript readout. None of that exists here. This is one window of the
 * story, looping, reporting nothing.
 *
 * Mounted by the row being played and by no other, so starting a second row
 * stops the first by construction — and so a story with a dozen speakers is one
 * media element rather than twelve. Playing on a click also means the gesture
 * the browser wants for unmuted playback is always there, so it carries sound.
 */
export const SpeakerMomentPreview: React.FC<SpeakerMomentPreviewProps> = ({
  mp4Url,
  muxPlaybackId,
  startTime,
  endTime,
  poster,
  label,
  isPlaying,
  onEnded,
}) => {
  const playerRef = useRef<MuxPlayerElement | null>(null);

  const handleTimeUpdate = (event: MuxPlayerElementEventMap['timeupdate']) => {
    const currentTime = (event.target as HTMLVideoElement).currentTime;
    // Never past the end of the turn: a speaker who holds the floor for four
    // seconds would otherwise spend a third of their preview playing whoever
    // spoke next, which is the thing the preview exists to disambiguate.
    const windowEnd = Math.min(startTime + PREVIEW_WINDOW_SECONDS, endTime);
    if (currentTime >= windowEnd - TIME_UPDATE_PADDING_SECONDS) {
      const player = playerRef.current;
      if (player) player.currentTime = startTime;
    }
  };

  // Play / pause from the row's control. Not `autoPlay`, which only speaks at
  // load: the row can be paused and resumed without remounting.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    // `play()` rejects when the element is not ready to start (a source still
    // resolving, an autoplay policy) — swallowed, because the row's control is
    // the only thing that says whether it is playing, and an unhandled
    // rejection here would surface as a console error on an ordinary click.
    if (isPlaying) player.play?.()?.catch(() => {});
    else player.pause?.();
  }, [isPlaying]);

  return (
    <Frame>
      <MuxPlayer
        onEnded={onEnded}
        ref={playerRef}
        style={playerStyle}
        streamType="on-demand"
        playbackId={muxPlaybackId}
        src={mp4Url}
        startTime={startTime}
        poster={poster}
        title={label}
        autoPlay
        volume={1}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
      />
    </Frame>
  );
};

const Frame = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: var(--radius-input);
  background-color: #000;
`;

export default SpeakerMomentPreview;
